// Friends — iter110 layout.
//
// Changes vs iter101:
//   • Unified bottom bar matching the map page style:
//       row 1 = [Add Friend][My QR], row 2 = standard BottomNav.
//   • Each accepted-friend card now ends with a slim full-width
//     "Show QR Code" button that opens that friend's QR sheet.
//   • Header is untouched (back arrow + title only — no Add Friend pill).
//
// New sheets (singletons mounted at the bottom of the page):
//   • FriendScanModal — fullscreen camera + scan-confirm card.
//   • QrSheet         — reused twice; one for the current user, one for
//                        whichever friend the user tapped.
//
// Sources unchanged: GET /api/friends and /api/me/qr.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, ChevronDown, MessageCircle, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import FriendsBottomBar from "../components/FriendsBottomBar";
import FriendScanModal from "../components/FriendScanModal";
import QrSheet from "../components/QrSheet";
import HeaderActions from "../components/HeaderActions";
import { useI18n } from "../lib/i18n";
import { EmptyState } from "./MyFavorites";

export default function Friends() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [friends, setFriends] = useState({ accepted: [], incoming: [], outgoing: [] });
  const [pendingOpen, setPendingOpen] = useState(false);

  // Sheet state
  const [scanOpen, setScanOpen] = useState(false);
  const [myQrOpen, setMyQrOpen] = useState(false);
  const [myQr, setMyQr] = useState(null);                // { qr_data }
  const [me, setMe] = useState(null);
  const [friendQr, setFriendQr] = useState(null);        // { user_id, display_name, profile_photo, view_preference }

  const load = () => {
    api.get("/friends").then(({ data }) => setFriends(data || { accepted: [], incoming: [], outgoing: [] })).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await getMe();
      if (cancelled) return;
      setMe(u);
      if (u) {
        try {
          const { data } = await api.get("/me/qr");
          if (!cancelled) setMyQr(data);
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accept = async (f) => {
    try {
      await api.post(`/friends/${f.id}/accept`);
      toast.success(t("friends_toast_connected"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const decline = async (f) => {
    try {
      await api.post(`/friends/${f.id}/decline`);
      toast.success(t("friends_toast_declined"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const pendingCount = friends.incoming.length;
  const pendingLabel = pendingCount === 1
    ? t("page_friends_pending_banner").replace("{n}", "1")
    : t("page_friends_pending_banner_plural").replace("{n}", String(pendingCount));

  const myUid = me?.user_id;
  const myQrUrl = myQr?.qr_data || (myUid ? `https://habano.app/u/${myUid}` : "");

  return (
    <main data-testid="friends-page" className="min-h-screen" style={{ background: "#0B0B0B", paddingBottom: 160 }}>
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} aria-label="Back" data-testid="friends-back"
          className="grid place-items-center"
          style={{ minHeight: 48, minWidth: 48, marginLeft: -8 }}>
          <ArrowLeft className="w-5 h-5 text-foreground/80" strokeWidth={1.5} />
        </button>
        <h1 className="flex-1 min-w-0" style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#F5F1E8" }}>
          {t("page_friends_title")}
        </h1>
        <HeaderActions user={me} testidPrefix="friends" />
      </header>

      {/* Pending requests banner */}
      {pendingCount > 0 && (
        <section className="px-5 mt-3" data-testid="friends-pending-banner">
          <button
            type="button"
            data-testid="friends-pending-toggle"
            onClick={() => setPendingOpen((o) => !o)}
            className="w-full flex items-center justify-between transition-colors hover:bg-white/[0.02]"
            style={{
              background: "rgba(232,184,75,0.06)",
              border: "1px solid rgba(232,184,75,0.35)",
              borderRadius: 12,
              padding: "12px 14px",
              minHeight: 48,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#E8B84B",
              fontWeight: 600,
            }}
          >
            <span>{pendingLabel}</span>
            <ChevronDown
              className="w-4 h-4"
              strokeWidth={1.5}
              style={{ transform: pendingOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}
            />
          </button>
          {pendingOpen && (
            <ul className="space-y-2 mt-2">
              {friends.incoming.map((f) => (
                <li key={f.id} className="lux-card p-3 flex items-center gap-3" data-testid={`friends-pending-${f.id}`}>
                  <FriendAvatar friend={f.other} />
                  <div className="flex-1 min-w-0 text-sm font-medium text-foreground truncate">
                    {f.other?.display_name}
                  </div>
                  <button
                    data-testid={`friends-pending-accept-${f.id}`}
                    onClick={() => accept(f)}
                    className="px-3 py-2 text-xs font-semibold"
                    style={{
                      background: "#E8B84B", color: "#0B0B0B",
                      borderRadius: 999, minHeight: 40,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {t("friends_accept")}
                  </button>
                  <button
                    data-testid={`friends-pending-decline-${f.id}`}
                    onClick={() => decline(f)}
                    className="px-3 py-2 text-xs"
                    style={{
                      background: "transparent", color: "#E8B84B",
                      border: "1px solid rgba(232,184,75,0.4)",
                      borderRadius: 999, minHeight: 40,
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {t("friends_decline")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Friends list */}
      <section className="px-5 mt-5" data-testid="friends-list">
        {friends.accepted.length === 0 ? (
          <EmptyState
            icon={Users}
            message={t("page_friends_empty")}
            testid="friends-empty"
          />
        ) : (
          <ul className="space-y-2">
            {friends.accepted.map((f) => (
              <li
                key={f.id}
                className="lux-card"
                data-testid={`friends-friend-${f.id}`}
                style={{ overflow: "hidden" }}
              >
                <div className="p-3 flex items-center gap-3">
                  <Link
                    to={`/friends/${f.other?.user_id}`}
                    data-testid={`friends-friend-open-${f.id}`}
                    className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-90 transition-opacity"
                    style={{ minHeight: 48 }}
                  >
                    <FriendAvatar friend={f.other} />
                    <div className="text-sm font-medium text-foreground flex-1 truncate"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                      {f.other?.display_name || "—"}
                    </div>
                  </Link>
                  {f.other?.whatsapp_shared && f.other?.whatsapp && (
                    <a
                      href={`https://wa.me/${(f.other.whatsapp || "").replace(/\D/g, "")}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`friends-friend-wa-${f.id}`}
                      aria-label="Open WhatsApp"
                      className="grid place-items-center rounded-full transition-colors"
                      style={{
                        minHeight: 44, minWidth: 44,
                        color: "#25d366",
                        border: "1px solid rgba(37,211,102,0.35)",
                      }}
                    >
                      <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`friends-friend-show-qr-${f.id}`}
                  onClick={() => setFriendQr({
                    user_id: f.other?.user_id,
                    display_name: f.other?.display_name,
                    profile_photo: f.other?.profile_photo,
                    view_preference: f.other?.view_preference,
                  })}
                  className="w-full inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-[rgba(232,184,75,0.08)]"
                  style={{
                    height: 32,
                    background: "rgba(232,184,75,0.04)",
                    borderTop: "0.5px solid #1f1f1f",
                    color: "#E8B84B",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                  }}
                >
                  <QrCode className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {t("friends_show_qr")}
                </button>
              </li>
            ))}
          </ul>
        )}
        {friends.accepted.length === 0 && lang && null}
      </section>

      <FriendsBottomBar
        onAddFriend={() => setScanOpen(true)}
        onShowMyQr={() => setMyQrOpen(true)}
      />

      <FriendScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onAdded={() => load()}
      />

      <QrSheet
        open={myQrOpen}
        onClose={() => setMyQrOpen(false)}
        kind="my"
        displayName={me?.display_name}
        profilePhoto={me?.profile_photo}
        viewPreference={me?.view_preference}
        qrUrl={myQrUrl}
      />

      <QrSheet
        open={!!friendQr}
        onClose={() => setFriendQr(null)}
        kind="friend"
        displayName={friendQr?.display_name}
        profilePhoto={friendQr?.profile_photo}
        viewPreference={friendQr?.view_preference}
        qrUrl={friendQr?.user_id ? `https://habano.app/u/${friendQr.user_id}` : ""}
      />
    </main>
  );
}

function FriendAvatar({ friend }) {
  if (friend?.profile_photo) {
    return (
      <img src={friend.profile_photo} alt=""
        className="w-12 h-12 rounded-full object-cover ring-1 ring-[rgba(232,184,75,0.4)] flex-shrink-0" />
    );
  }
  const initials = (friend?.display_name || "?").slice(0, 2).toUpperCase();
  return (
    <span
      className="grid place-items-center rounded-full flex-shrink-0"
      style={{
        width: 48, height: 48,
        background: "#171717",
        color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 14, fontWeight: 600,
        border: "1px solid rgba(232,184,75,0.35)",
      }}
    >
      {initials}
    </span>
  );
}
